/**
 * Loris, Beaut Lover or ALM Perfume buy → auto-add 1 free ALM 5ml gift (max 1 per order, whichever brands are in cart);
 * remove gift when the last qualifying item is gone.
 * Also intercepts Buy Now / accelerated checkout / cart checkout so gift is present at checkout.
 */
(function initAlmLorisGift() {
  const GIFT_PROP = '_alm_loris_gift';
  const SOURCE = 'alm-loris-gift';

  /** @type {boolean} */
  let syncing = false;
  /** @type {number} */
  let queued = 0;
  /** @type {Promise<void> | null} */
  let syncPromise = null;
  /** @type {boolean} */
  let interceptingCheckout = false;

  /**
   * @returns {{ available: number[], all: number[], qualifyingProductIds: number[], isEligibleProduct: boolean, checkoutUrl: string }}
   */
  function getConfig() {
    const cfg = window.ALM_LORIS_GIFT || {};
    const available = Array.isArray(cfg.availableVariantIds)
      ? cfg.availableVariantIds.map(Number).filter(Boolean)
      : [];
    const all = Array.isArray(cfg.variantIds) ? cfg.variantIds.map(Number).filter(Boolean) : [];
    const qualifyingProductIds = Array.isArray(cfg.qualifyingProductIds)
      ? cfg.qualifyingProductIds.map(Number).filter(Boolean)
      : [];
    return {
      available,
      all: all.length ? all : available,
      qualifyingProductIds,
      isEligibleProduct: Boolean(cfg.isGiftEligibleProduct || cfg.isLorisProduct),
      checkoutUrl: cfg.checkoutUrl || '/checkout',
    };
  }

  /**
   * @param {number[]} list
   * @returns {number | null}
   */
  function pickRandom(list) {
    if (!list.length) return null;
    return list[Math.floor(Math.random() * list.length)];
  }

  /**
   * @param {object} item
   * @returns {boolean}
   */
  function isGiftItem(item) {
    const props = item.properties || {};
    return props[GIFT_PROP] === '1' || props[GIFT_PROP] === 1;
  }

  /**
   * @param {object} item
   * @returns {boolean}
   */
  function isQualifyingItem(item) {
    if (isGiftItem(item)) return false;
    // A paid ALM 5ml test version never unlocks the free gift.
    if (getConfig().all.includes(Number(item.variant_id))) return false;
    if (getConfig().qualifyingProductIds.includes(Number(item.product_id))) return true;
    const vendor = String(item.vendor || '')
      .trim()
      .toLowerCase();
    if (vendor === 'loris') return true;
    const title = String(item.product_title || item.title || '').toLowerCase();
    return title.startsWith('loris ') || title.includes(' loris ');
  }

  /**
   * @returns {Promise<object>}
   */
  async function fetchCart() {
    const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to load cart');
    return response.json();
  }

  /**
   * @returns {string[]}
   */
  function getSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
  }

  /**
   * @param {object} cart
   * @param {Record<string, string> | undefined} sections
   */
  function dispatchCartUpdate(cart, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data: {
            source: SOURCE,
            itemCount: cart.item_count,
            sections,
          },
        },
      })
    );
  }

  /**
   * @param {number} variantId
   * @returns {Promise<object>}
   */
  async function addGift(variantId) {
    const sectionIds = getSectionIds();
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [GIFT_PROP]: '1',
            },
          },
        ],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      const err = new Error(data.description || data.message || 'Unable to add free gift');
      // @ts-ignore
      err.payload = data;
      throw err;
    }
    return data;
  }

  /**
   * @param {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} items
   */
  async function addItems(items) {
    const sectionIds = getSectionIds();
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to add items');
    }
    return data;
  }

  /**
   * @param {string} key
   * @returns {Promise<object>}
   */
  async function removeLine(key) {
    const sectionIds = getSectionIds();
    const changeUrl = String(window.Theme?.routes?.cart_change_url || '/cart/change').replace(/\.js$/, '') + '.js';
    const response = await fetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: 0,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to remove free gift');
    }
    return data;
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<object>}
   */
  async function setQuantity(key, quantity) {
    const sectionIds = getSectionIds();
    const changeUrl = String(window.Theme?.routes?.cart_change_url || '/cart/change').replace(/\.js$/, '') + '.js';
    const response = await fetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    return response.json();
  }

  /**
   * @returns {number | null}
   */
  function pickGiftVariantId() {
    const { available, all } = getConfig();
    return pickRandom(available.length ? available : all);
  }

  async function syncGift() {
    if (syncPromise) {
      queued += 1;
      return syncPromise;
    }

    syncing = true;
    syncPromise = (async () => {
      let lastCart = null;
      let lastSections = undefined;
      let cartChanged = false;

      try {
        const cart = await fetchCart();
        lastCart = cart;
        const hasQualifying = (cart.items || []).some(isQualifyingItem);
        const giftItems = (cart.items || []).filter(isGiftItem);

        if (!hasQualifying) {
          for (const gift of giftItems) {
            const updated = await removeLine(gift.key);
            lastCart = updated;
            lastSections = updated.sections;
            cartChanged = true;
          }
        } else if (giftItems.length === 0) {
          const preferred = pickGiftVariantId();
          if (!preferred) {
            console.warn('[ALM free gift] No ALM 5ml variants configured');
          } else {
            const { all } = getConfig();
            try {
              const added = await addGift(preferred);
              lastSections = added.sections;
              lastCart = await fetchCart();
              cartChanged = true;
            } catch (firstError) {
              const fallbackPool = all.filter((id) => id !== preferred);
              const fallback = pickRandom(fallbackPool);
              if (!fallback) throw firstError;
              const added = await addGift(fallback);
              lastSections = added.sections;
              lastCart = await fetchCart();
              cartChanged = true;
            }
          }
        } else {
          const [keep, ...extras] = giftItems;
          if (keep.quantity !== 1) {
            const updated = await setQuantity(keep.key, 1);
            lastCart = updated;
            lastSections = updated.sections;
            cartChanged = true;
          }
          for (const extra of extras) {
            const updated = await removeLine(extra.key);
            lastCart = updated;
            lastSections = updated.sections;
            cartChanged = true;
          }
          if (!lastCart) lastCart = await fetchCart();
        }

        // Only notify theme when gift lines actually changed — avoids opening cart drawer on every page
        if (cartChanged && lastCart) {
          dispatchCartUpdate(lastCart, lastSections);
        }
      } catch (error) {
        console.warn('[ALM free gift]', error);
      } finally {
        syncing = false;
        syncPromise = null;
        if (queued > 0) {
          queued = 0;
          queueMicrotask(() => {
            syncGift();
          });
        }
      }
    })();

    return syncPromise;
  }

  /**
   * Public: wait until gift sync finishes.
   * @returns {Promise<void>}
   */
  async function ensureGift() {
    await syncGift();
    // If another sync was queued while we ran, wait for the next cycle once.
    if (syncPromise) await syncPromise;
  }

  /**
   * @returns {{ variantId: number, quantity: number } | null}
   */
  function getProductFormSelection() {
    const form =
      document.querySelector('product-form-component form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) return null;

    const idInput =
      form.querySelector('input[name="id"]:checked') ||
      form.querySelector('select[name="id"]') ||
      form.querySelector('input[name="id"]');
    const qtyInput = form.querySelector('input[name="quantity"]');

    const variantId = Number(
      idInput instanceof HTMLInputElement || idInput instanceof HTMLSelectElement ? idInput.value : 0
    );
    const quantity = Math.max(
      1,
      Number(qtyInput instanceof HTMLInputElement ? qtyInput.value : 1) || 1
    );

    if (!variantId) return null;
    return { variantId, quantity };
  }

  /**
   * Add current eligible product + free gift, then go to checkout.
   * Used when Buy Now / accelerated checkout would otherwise skip the gift.
   */
  async function buyNowWithGift() {
    const selection = getProductFormSelection();
    const giftId = pickGiftVariantId();
    if (!selection) throw new Error('Missing variant');
    if (!giftId) throw new Error('Missing gift variant');

    const cart = await fetchCart();
    const alreadyHasVariant = (cart.items || []).some(
      (item) => Number(item.variant_id) === selection.variantId && !isGiftItem(item)
    );
    const alreadyHasGift = (cart.items || []).some(isGiftItem);
    const selectionIs5ml = getConfig().all.includes(selection.variantId);

    /** @type {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} */
    const items = [];
    if (!alreadyHasVariant) {
      items.push({ id: selection.variantId, quantity: selection.quantity });
    }
    if (!alreadyHasGift && !selectionIs5ml) {
      items.push({
        id: giftId,
        quantity: 1,
        properties: { [GIFT_PROP]: '1' },
      });
    }

    if (items.length) {
      await addItems(items);
    } else {
      await ensureGift();
    }

    // Normalize: one gift only
    await ensureGift();
    window.location.href = getConfig().checkoutUrl;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  function getCheckoutTrigger(target) {
    if (!(target instanceof Element)) return null;
    return target.closest(
      [
        '.sticky-add-to-cart__buy-now',
        '.shopify-payment-button__button',
        'shopify-accelerated-checkout',
        'shopify-accelerated-checkout-cart',
        '[name="checkout"]',
        'button[name="checkout"]',
        'input[name="checkout"]',
        '#checkout',
      ].join(', ')
    );
  }

  /**
   * @param {MouseEvent} event
   */
  async function onCheckoutIntent(event) {
    if (interceptingCheckout) return;
    if (event.defaultPrevented) return;

    const trigger = getCheckoutTrigger(event.target);
    if (!trigger) return;

    // Ignore disabled buttons
    if (trigger instanceof HTMLButtonElement && trigger.disabled) return;
    if (trigger.getAttribute('aria-disabled') === 'true') return;
    if (trigger.hasAttribute('disabled')) return;

    const { isEligibleProduct } = getConfig();
    let needsIntercept = isEligibleProduct;

    if (!needsIntercept) {
      try {
        const cart = await fetchCart();
        const hasQualifying = (cart.items || []).some(isQualifyingItem);
        const hasGift = (cart.items || []).some(isGiftItem);
        needsIntercept = hasQualifying && !hasGift;
      } catch {
        needsIntercept = false;
      }
    }

    if (!needsIntercept) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    interceptingCheckout = true;
    try {
      if (isEligibleProduct) {
        await buyNowWithGift();
      } else {
        await ensureGift();
        window.location.href = getConfig().checkoutUrl;
      }
    } catch (error) {
      console.warn('[ALM free gift] checkout intercept failed', error);
      // Fallback: still try checkout so user is not blocked
      window.location.href = getConfig().checkoutUrl;
    } finally {
      interceptingCheckout = false;
    }
  }

  /** @param {Event} event */
  function onCartUpdate(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail?.data?.source === SOURCE || detail?.sourceId === SOURCE) return;
    syncGift();
  }

  // Capture-phase so we run before Shopify accelerated checkout handlers
  document.addEventListener('click', onCheckoutIntent, true);
  document.addEventListener('pointerdown', (event) => {
    // Mark gift-eligible buy-now intents early; click handler does the work
    const trigger = getCheckoutTrigger(event.target);
    if (trigger && getConfig().isEligibleProduct) {
      trigger.setAttribute('data-alm-gift-pending', '1');
    }
  }, true);

  document.addEventListener(
    'submit',
    async (event) => {
      if (interceptingCheckout) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = event.submitter;
      const isCheckout =
        (submitter instanceof HTMLElement &&
          (submitter.getAttribute('name') === 'checkout' || submitter.id === 'checkout')) ||
        Boolean(form.querySelector('[name="checkout"]'));
      if (!isCheckout) return;

      try {
        const cart = await fetchCart();
        const hasQualifying = (cart.items || []).some(isQualifyingItem);
        const hasGift = (cart.items || []).some(isGiftItem);
        if (!hasQualifying || hasGift) return;
      } catch {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      interceptingCheckout = true;
      try {
        await ensureGift();
        window.location.href = getConfig().checkoutUrl;
      } catch (error) {
        console.warn('[ALM free gift] checkout submit intercept failed', error);
        window.location.href = getConfig().checkoutUrl;
      } finally {
        interceptingCheckout = false;
      }
    },
    true
  );

  document.addEventListener('cart:update', onCartUpdate);

  // Expose for sticky ATC / other theme scripts
  window.ALM_LORIS_GIFT = Object.assign(window.ALM_LORIS_GIFT || {}, {
    ensureGift,
    syncGift,
    buyNowWithGift,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncGift();
    });
  } else {
    syncGift();
  }
})();
